import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Box,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper as MuiPaper
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import WorkIcon from '@mui/icons-material/Work';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder
} from "@mui/icons-material";

// Separate section orders for left and right sides
const defaultLeftSectionOrder = [
  'about',
  'contact',
  'skills',
  'languages',
  'education'
];

const defaultRightSectionOrder = [
  'experience',
  'projects',
  'softwareSkills',
  'interests',
  'additionalInfo',
  'references',
  'websites'
];

// Page size mapping
const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const Resume11 = ({ 
  color = '#36454F',
  font = "Arial, sans-serif",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 30,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 30,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  theme,
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true
}) => {
  // Use theme color if provided, otherwise use the color prop
  const primaryColor = theme?.primary || color;
  const darkColor = theme?.dark || getDarkColor(primaryColor);
  
  // State for fetched data
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const mainContentRef = useRef(null);
  const measureContainerRef = useRef(null);

  // State for section ordering - separate for left and right
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  
  // State for arrange sections dialog
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Default data
  const defaultData = {
    firstName: "John",
    lastName: "Doe",
    currentPosition: "Senior Graphic Designer",
    phone: "+1 (555) 123-4567",
    email: "johndoe@email.com",
    city: "New York, NY",
    website: "linkedin.com/in/johndoe",
    linkedIn: "",
    drivingLicence: "",
    profile: "A creative and results-driven graphic designer with over 7 years of experience in visual branding and marketing. Passionate about creating designs that solve business problems and delight customers."
  };

  const data = { ...defaultData, ...formData };
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  // Convert mm to pixels (1mm = 3.779527559px)
  const mmToPx = (mm) => mm * 3.779527559;
  
  // Get page dimensions in pixels
  const pageWidthPx = mmToPx(parseFloat(page.width));
  const pageHeightPx = mmToPx(parseFloat(page.height));
  const contentWidthPx = pageWidthPx - (2 * sideMargins);
  const leftColumnWidthPx = contentWidthPx * 0.3;
  const rightColumnWidthPx = contentWidthPx * 0.7;
  
  // Helper function to darken colors
  function getDarkColor(hex) {
    if (!hex) return '#000000';
    let c = hex.replace('#', '');
    if (c.length === 8) c = c.slice(0, 6);
    if (c.length !== 6) return '#000000';
    let r = Math.max(0, parseInt(c.substring(0,2),16) - 40);
    let g = Math.max(0, parseInt(c.substring(2,4),16) - 40);
    let b = Math.max(0, parseInt(c.substring(4,6),16) - 40);
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  }

  // Format date helper
  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Load data from localStorage
  useEffect(() => {
    const loadData = () => {
      console.log("🔄 Loading resume data from localStorage...");
      
      // Load basic data
      try {
        const storedEntries = localStorage.getItem("educationEntries");
        if (storedEntries) {
          setEducationEntries(JSON.parse(storedEntries));
        }
      } catch(e) { console.error("Error loading educationEntries", e); }

      try {
        const storedSkills = localStorage.getItem("skills");
        if (storedSkills) {
          setSavedSkills(JSON.parse(storedSkills));
        }
      } catch(e) { console.error("Error loading skills", e); }

      try {
        const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
        if (storedSummaries.length > 0) {
          setSavedSummary(storedSummaries[storedSummaries.length - 1]);
        }
      } catch(e) { console.error("Error loading summaries", e); }

      try {
        const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
        setSavedAdditionalInfo(storedAdditionalInfo);
      } catch(e) { console.error("Error loading additionalInfo", e); }

      try {
        const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
        setSavedLanguages(storedLanguages);
      } catch(e) { console.error("Error loading languagesList", e); }

      try {
        const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
        setSavedAccomplishments(storedAccomplishments);
      } catch(e) { console.error("Error loading accomplishmentsList", e); }

      try {
        const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
        setSavedCertifications(storedCertifications);
      } catch(e) { console.error("Error loading certificationsList", e); }

      try {
        const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
        setSavedReferences(storedReferences);
      } catch(e) { console.error("Error loading referencesList", e); }

      try {
        const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
        setSoftwareSkills(storedSoftwareSkills);
      } catch(e) { console.error("Error loading softwareSkills", e); }

      try {
        const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
        setSavedVolunteering(storedVolunteering);
      } catch(e) { console.error("Error loading volunteering", e); }

      try {
        const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
        setSavedInterests(storedInterests);
      } catch(e) { console.error("Error loading interests", e); }

      try {
        const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
        setSavedWebsites(storedWebsites);
      } catch(e) { console.error("Error loading websites", e); }
      
      // Load custom sections
      let sectionsData = {};
      try {
        const userId = localStorage.getItem("userId");
        console.log("👤 Current userId:", userId);
        
        if (userId) {
          const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
          console.log("🔍 Current custom sections to display:", currentCustomSections);
          
          if (currentCustomSections.length > 0) {
            currentCustomSections.forEach(sectionName => {
              const userCustomKey = `custom_${userId}_${sectionName}`;
              try {
                const customSectionData = localStorage.getItem(userCustomKey);
                if (customSectionData) {
                  const parsedData = JSON.parse(customSectionData);
                  const items = parsedData.items || [];
                  const validItems = items.filter(item => item && item.trim() !== '');
                  
                  if (validItems.length > 0) {
                    sectionsData[sectionName] = validItems;
                    console.log(`✅ Loaded current custom section: ${sectionName} with ${validItems.length} items`);
                  }
                }
              } catch (error) {
                console.error(`❌ Error loading current custom section ${sectionName}:`, error);
              }
            });
          }
        }
      } catch(e) { console.error("Error loading custom sections", e); }

      setCustomSections(sectionsData);

      // Load section orders with custom sections handling
      try {
        const savedLeftSectionOrder = localStorage.getItem("resume11LeftSectionOrder");
        const savedRightSectionOrder = localStorage.getItem("resume11RightSectionOrder");
        
        const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
        
        // Handle left section order
        if (savedLeftSectionOrder) {
          const parsedOrder = JSON.parse(savedLeftSectionOrder);
          const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
          setLeftSectionOrder(filteredOrder);
        } else {
          setLeftSectionOrder(defaultLeftSectionOrder);
        }

        // Handle right section order
        if (savedRightSectionOrder) {
          const parsedOrder = JSON.parse(savedRightSectionOrder);
          const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
          const allRightSections = [...filteredOrder, ...currentCustomSectionIds];
          setRightSectionOrder(allRightSections);
        } else {
          const updatedRightSectionOrder = [...defaultRightSectionOrder, ...currentCustomSectionIds];
          setRightSectionOrder(updatedRightSectionOrder);
        }
      } catch(e) { console.error("Error loading section orders", e); }
    };

    loadData();
  }, []);

  // Save section orders to localStorage
  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      try {
        localStorage.setItem("resume11LeftSectionOrder", JSON.stringify(leftSectionOrder));
      } catch(e) {}
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      try {
        localStorage.setItem("resume11RightSectionOrder", JSON.stringify(rightSectionOrder));
      } catch(e) {}
    }
  }, [rightSectionOrder]);

  // Update custom sections in right order when customSections changes
  useEffect(() => {
    try {
      const userId = localStorage.getItem("userId");
      if (userId && Object.keys(customSections).length > 0) {
        const currentRightOrder = [...rightSectionOrder];
        const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
        const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
        const newRightOrder = [...filteredRightOrder, ...currentCustomSectionIds];
        if (JSON.stringify(newRightOrder) !== JSON.stringify(currentRightOrder)) {
          setRightSectionOrder(newRightOrder);
          localStorage.setItem("resume11RightSectionOrder", JSON.stringify(newRightOrder));
        }
      }
    } catch(e) {}
  }, [customSections]);

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'left',
      sectionId: leftSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'right',
      sectionId: rightSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    try {
      const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const sourceIndex = dropData.index;
      const sourceSide = dropData.side;
      const sectionId = dropData.sectionId;

      if (sourceSide === 'left' && sourceIndex !== targetIndex) {
        const newSectionOrder = [...leftSectionOrder];
        const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
        newSectionOrder.splice(targetIndex, 0, movedSection);
        setLeftSectionOrder(newSectionOrder);
      } else if (sourceSide === 'right') {
        const newLeftSectionOrder = [...leftSectionOrder];
        const newRightSectionOrder = [...rightSectionOrder];
        
        newRightSectionOrder.splice(sourceIndex, 1);
        newLeftSectionOrder.splice(targetIndex, 0, sectionId);
        
        setLeftSectionOrder(newLeftSectionOrder);
        setRightSectionOrder(newRightSectionOrder);
      }
    } catch(err) {
      console.error("Drop error:", err);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    if (e.currentTarget) e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    try {
      const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const sourceIndex = dropData.index;
      const sourceSide = dropData.side;
      const sectionId = dropData.sectionId;

      if (sourceSide === 'right' && sourceIndex !== targetIndex) {
        const newSectionOrder = [...rightSectionOrder];
        const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
        newSectionOrder.splice(targetIndex, 0, movedSection);
        setRightSectionOrder(newSectionOrder);
      } else if (sourceSide === 'left') {
        const newLeftSectionOrder = [...leftSectionOrder];
        const newRightSectionOrder = [...rightSectionOrder];
        
        newLeftSectionOrder.splice(sourceIndex, 1);
        newRightSectionOrder.splice(targetIndex, 0, sectionId);
        
        setLeftSectionOrder(newLeftSectionOrder);
        setRightSectionOrder(newRightSectionOrder);
      }
    } catch(err) {
      console.error("Drop error:", err);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    if (e.currentTarget) e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    if (e.currentTarget) e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
  };

  const resetRightSectionOrder = () => {
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const handleArrangeSectionsClick = () => {
    setArrangeDialogOpen(true);
  };

  const handleArrangeDialogClose = () => {
    setArrangeDialogOpen(false);
  };

  // Common typography styles function
  const getTypographyStyles = (isHeading = false, isSidebar = false, customColor = null) => {
    const baseStyles = {
      fontFamily: font,
      fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`,
    };

    if (isHeading) {
      return {
        ...baseStyles,
        fontSize: headingSize,
        fontWeight: "bold",
        color: customColor || (isSidebar ? '#F8F8F8' : '#333333'),
        mb: sectionSpacing / 10,
      };
    }

    return {
      ...baseStyles,
      fontSize: fontSize,
      color: customColor || (isSidebar ? '#F8F8F8' : '#333333'),
      textIndent: `${paragraphIndent}px`,
      mb: paragraphSpacing,
    };
  };

  // Common box container styles
  const getContainerStyles = (isSidebar = false) => ({
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    ...(isSidebar && {
      bgcolor: primaryColor,
      color: '#F8F8F8',
    })
  });

  // Render left sidebar content as HTML string for measurement
  const renderLeftSectionHTML = (sectionId) => {
    const textColor = '#F8F8F8';
    
    switch (sectionId) {
      case 'about':
      case 'aboutMe':
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
            <div style="text-align: center; margin-bottom: ${sectionSpacing / 10}px;">
              <img src="${photo || ''}" alt="Profile" style="width: 120px; height: 120px; border-radius: 50%; margin: 0 auto; border: ${lineWeight * 2}px solid ${textColor}; object-fit: cover;" />
            </div>
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">About Me</div>
            <div style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">${savedSummary || data.profile}</div>
          </div>
        `;

      case 'contact':
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Contact</div>
            ${data.phone ? `<div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;"><span style="margin-right: 8px;">📞</span> <span>${data.phone}</span></div>` : ''}
            ${data.email ? `<div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;"><span style="margin-right: 8px;">✉️</span> <span>${data.email}</span></div>` : ''}
            ${data.city ? `<div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;"><span style="margin-right: 8px;">📍</span> <span>${data.city}</span></div>` : ''}
            ${data.website ? `<div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;"><span style="margin-right: 8px;">🌐</span> <a href="${data.website}" style="color: ${textColor}; text-decoration: none;">${data.website.replace(/^https?:\/\/(www\.)?/, '')}</a></div>` : ''}
            ${data.linkedIn ? `<div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;"><span style="margin-right: 8px;">🔗</span> <a href="${data.linkedIn}" style="color: ${textColor}; text-decoration: none;">${data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}</a></div>` : ''}
          </div>
        `;

      case 'skills':
        const skillsList = savedSkills.length > 0 ? savedSkills : [{name: 'Adobe Creative Suite'}, {name: 'UI/UX Design'}, {name: 'Branding & Identity'}, {name: 'Print & Digital Media'}];
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Skills</div>
            ${skillsList.map(skill => `<div style="margin-bottom: ${paragraphSpacing}px;">• ${skill.name}</div>`).join('')}
          </div>
        `;

      case 'languages':
        if (savedLanguages.length === 0) return '';
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Languages</div>
            ${savedLanguages.map(lang => `<div style="margin-bottom: ${paragraphSpacing}px;">• ${lang.name} ${lang.level ? `- ${lang.level}` : ''}</div>`).join('')}
          </div>
        `;

      case 'education':
        if (educationEntries.length === 0) {
          return `
            <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
              <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Education</div>
              <div style="font-weight: bold;">B.F.A. in Graphic Design</div>
              <div>New York School of Design</div>
              <div>Graduated: May 2018</div>
            </div>
          `;
        }
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Education</div>
            ${educationEntries.map(edu => {
              if (edu.educationLevel === "school") {
                return `
                  <div style="margin-bottom: ${paragraphSpacing}px;">
                    <div style="font-weight: bold;">${edu.standard} Standard ${edu.stream ? `- ${edu.stream}` : ''}</div>
                    <div>${edu.schoolName} ${edu.schoolLocation ? `(${edu.schoolLocation})` : ''}</div>
                    ${edu.percentage ? `<div>Percentage: ${edu.percentage}%</div>` : ''}
                  </div>
                `;
              } else if (edu.educationLevel === "college") {
                return `
                  <div style="margin-bottom: ${paragraphSpacing}px;">
                    <div style="font-weight: bold;">${edu.degreeName} ${edu.specialization ? `- ${edu.specialization}` : ''}</div>
                    <div>${edu.collegeName} ${edu.collegeLocation ? `(${edu.collegeLocation})` : ''}</div>
                    <div>${edu.cgpa ? `CGPA: ${edu.cgpa}` : ''}${edu.grade ? (edu.cgpa ? ` | Grade: ${edu.grade}` : `Grade: ${edu.grade}`) : ''}</div>
                    ${edu.gradMonth && edu.gradYear ? `<div>Graduated: ${edu.gradMonth} ${edu.gradYear}</div>` : ''}
                  </div>
                `;
              }
              return '';
            }).join('')}
          </div>
        `;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items.length === 0) return '';
          return `
            <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; background-color: ${primaryColor}; color: ${textColor};">
              <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">${customSectionName.toUpperCase()}</div>
              ${items.map(item => `<div style="margin-bottom: ${paragraphSpacing}px;">• ${item}</div>`).join('')}
            </div>
          `;
        }
        return '';
    }
  };

  // Render right content sections as HTML string for measurement
  const renderRightSectionHTML = (sectionId) => {
    const textColor = '#333333';
    
    switch (sectionId) {
      case 'experience':
        if (workExperiences && workExperiences.length > 0) {
          return `
            <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
              <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Experience</div>
              ${workExperiences.map(work => `
                <div style="margin-bottom: ${sectionSpacing / 10}px;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="font-weight: bold;">${work.jobTitle}</div>
                    <div style="font-style: italic;">${formatDate(work.startMonth, work.startYear)} - ${work.current ? "Present" : formatDate(work.endMonth, work.endYear)}</div>
                  </div>
                  <div style="font-style: italic; margin-bottom: ${paragraphSpacing}px;">${work.employer} | ${work.location || ''}</div>
                  <div>${work.description || "No description provided."}</div>
                </div>
              `).join('')}
            </div>
          `;
        }
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Experience</div>
            <div style="margin-bottom: ${sectionSpacing / 10}px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div style="font-weight: bold;">Lead Designer</div>
                <div style="font-style: italic;">Jan 2022 - Present</div>
              </div>
              <div style="font-style: italic; margin-bottom: ${paragraphSpacing}px;">Creative Solutions Inc. | New York, NY</div>
              <div>• Led a team of 5 designers on major branding projects, increasing client satisfaction by 25%.</div>
              <div>• Developed and executed design strategies that resulted in a 15% growth in new business.</div>
            </div>
          </div>
        `;

      case 'projects':
        const hasCertifications = savedCertifications.length > 0;
        const hasAccomplishments = savedAccomplishments.length > 0;
        const hasVolunteering = savedVolunteering.length > 0;
        
        if (!hasCertifications && !hasAccomplishments && !hasVolunteering) {
          return `
            <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
              <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Projects & Accomplishments</div>
              <div style="margin-bottom: ${(paragraphSpacing * 2)}px;">
                <div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;">
                  <span style="margin-right: 8px;">💼</span>
                  <div style="font-weight: bold;">E-commerce Brand Redesign</div>
                </div>
                <div>• Led the full visual redesign of an e-commerce platform, improving user experience and increasing conversion rates by 10%.</div>
                <div>• Created a comprehensive brand style guide, including new logo, color palette, and typography.</div>
              </div>
              <div>
                <div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;">
                  <span style="margin-right: 8px;">💼</span>
                  <div style="font-weight: bold;">Mobile App UI/UX</div>
                </div>
                <div>• Designed the complete user interface and user experience for a new mobile application.</div>
                <div>• Conducted user research and usability testing to inform design decisions and improve flow.</div>
              </div>
            </div>
          `;
        }
        
        let html = `<div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">`;
        
        if (savedCertifications.length > 0) {
          html += `<div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Certifications</div>`;
          html += savedCertifications.map(cert => `
            <div style="margin-bottom: ${(paragraphSpacing * 2)}px;">
              <div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;">
                <span style="margin-right: 8px;">📜</span>
                <div style="font-weight: bold;">${cert.name} - ${cert.provider}</div>
              </div>
              <div>• Completed in ${cert.year}</div>
            </div>
          `).join('');
        }
        
        if (savedAccomplishments.length > 0) {
          html += `<div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Accomplishments</div>`;
          html += savedAccomplishments.map(acc => `
            <div style="margin-bottom: ${(paragraphSpacing * 2)}px;">
              <div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;">
                <span style="margin-right: 8px;">🏆</span>
                <div style="font-weight: bold;">Accomplishment</div>
              </div>
              <div>${acc}</div>
            </div>
          `).join('');
        }
        
        if (savedVolunteering.length > 0) {
          html += `<div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Volunteering</div>`;
          html += savedVolunteering.map(vol => `
            <div style="margin-bottom: ${(paragraphSpacing * 2)}px;">
              <div style="display: flex; align-items: center; margin-bottom: ${paragraphSpacing}px;">
                <span style="margin-right: 8px;">🤝</span>
                <div style="font-weight: bold;">${vol.subtopic}</div>
              </div>
              <div>${vol.content}</div>
            </div>
          `).join('');
        }
        
        html += `</div>`;
        return html;

      case 'softwareSkills':
        if (softwareSkills.length === 0) return '';
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Software Skills</div>
            ${softwareSkills.map(skill => `<div>${skill.name} — ${skill.level}%</div>`).join('')}
          </div>
        `;

      case 'interests':
        if (savedInterests.length === 0) return '';
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Interests</div>
            ${savedInterests.map(interest => `<div>• ${interest}</div>`).join('')}
          </div>
        `;

      case 'additionalInfo':
        if (savedAdditionalInfo.length === 0) return '';
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Additional Information</div>
            ${savedAdditionalInfo.map(info => `<div>${info}</div>`).join('')}
          </div>
        `;

      case 'references':
        if (savedReferences.length === 0) return '';
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">References</div>
            ${savedReferences.map(ref => `<div>${ref}</div>`).join('')}
          </div>
        `;

      case 'websites':
        if (savedWebsites.length === 0) return '';
        return `
          <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
            <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">Websites / Profiles</div>
            ${savedWebsites.map(site => `<div><a href="${site.url}" style="color: ${textColor}; text-decoration: none;">${site.name || site.url}</a></div>`).join('')}
          </div>
        `;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items.length === 0) return '';
          return `
            <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; color: ${textColor};">
              <div style="font-family: ${font}; font-weight: bold; font-size: ${headingSize}; color: ${textColor}; margin-bottom: ${sectionSpacing / 10}px;">${customSectionName.toUpperCase()}</div>
              ${items.map(item => `<div>• ${item}</div>`).join('')}
            </div>
          `;
        }
        return '';
    }
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'about': 'About Me',
      'aboutMe': 'About Me',
      'contact': 'Contact',
      'skills': 'Skills',
      'languages': 'Languages',
      'education': 'Education',
      'experience': 'Experience',
      'projects': 'Projects & Accomplishments',
      'softwareSkills': 'Software Skills',
      'interests': 'Interests',
      'additionalInfo': 'Additional Information',
      'references': 'References',
      'websites': 'Websites/Profiles',
      'volunteering': 'Volunteering',
    };
    
    if (sectionId.startsWith('custom_')) {
      return sectionId.replace('custom_', '').toUpperCase();
    }
    
    return titles[sectionId] || sectionId;
  };

  // Main pagination function - FIXED: added null checks for sectionElement
  const paginateContent = useCallback(() => {
    const headerHTML = `
      <div style="margin-bottom: ${sectionSpacing / 10}px; font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;">
        <div style="color: ${primaryColor}; font-family: ${font}; font-weight: bold; font-size: calc(${headingSize} * 1.8); font-style: ${fontStyle}; line-height: ${lineSpacing * 1.2}px;">${data.firstName ? data.firstName.toUpperCase() : ''} ${data.lastName ? data.lastName.toUpperCase() : ''}</div>
        <div style="color: #333333; font-family: ${font}; font-size: calc(${fontSize} * 1.3); font-style: ${fontStyle}; line-height: ${lineSpacing}px; margin-top: 4px; margin-bottom: 4px;">${data.currentPosition || ''}</div>
      </div>
      <div style="margin: ${sectionSpacing / 10}px 0; border-width: ${lineWeight}px; border-style: solid; border-color: #e0e0e0;"></div>
    `;
    
    const rightSectionsHTML = rightSectionOrder.map(sectionId => renderRightSectionHTML(sectionId)).filter(html => html !== '');
    const leftSectionsHTML = leftSectionOrder.map(sectionId => renderLeftSectionHTML(sectionId)).filter(html => html !== '');

    // Create temporary container for measurement
    const measureContainer = document.createElement('div');
    measureContainer.style.position = 'absolute';
    measureContainer.style.visibility = 'hidden';
    measureContainer.style.top = '-9999px';
    measureContainer.style.left = '-9999px';
    measureContainer.style.width = `${pageWidthPx}px`;
    measureContainer.style.fontFamily = font;
    document.body.appendChild(measureContainer);

    let headerHeight = 120;
    try {
      measureContainer.innerHTML = headerHTML;
      const headerElement = measureContainer.firstElementChild;
      if (headerElement) {
        headerHeight = headerElement.offsetHeight || 120;
      }
    } catch(e) {
      console.error("Error measuring header:", e);
    }

    // Calculate available content height per page
    const availableHeightPerPage = pageHeightPx - (2 * topBottomMargin);
    const availableMainHeightFirstPage = availableHeightPerPage - headerHeight - 40;
    const availableMainHeightOtherPages = availableHeightPerPage - 40;

    // Measure and paginate right sections - FIXED: null check on sectionElement
    const rightSectionsWithHeights = [];
    for (const sectionHTML of rightSectionsHTML) {
      try {
        measureContainer.innerHTML = sectionHTML;
        const sectionElement = measureContainer.firstElementChild;
        if (sectionElement) {
          sectionElement.style.width = `${rightColumnWidthPx}px`;
          const height = sectionElement.offsetHeight || 0;
          rightSectionsWithHeights.push({ html: sectionHTML, height });
        }
      } catch(e) {
        console.error("Error measuring right section:", e);
      }
    }

    // Measure and paginate left sections - FIXED: null check on sectionElement
    const leftSectionsWithHeights = [];
    for (const sectionHTML of leftSectionsHTML) {
      try {
        measureContainer.innerHTML = sectionHTML;
        const sectionElement = measureContainer.firstElementChild;
        if (sectionElement) {
          sectionElement.style.width = `${leftColumnWidthPx}px`;
          const height = sectionElement.offsetHeight || 0;
          leftSectionsWithHeights.push({ html: sectionHTML, height });
        }
      } catch(e) {
        console.error("Error measuring left section:", e);
      }
    }

    // Clean up
    try {
      document.body.removeChild(measureContainer);
    } catch(e) {}

    // Paginate right sections
    const rightPages = [];
    let currentRightPage = [];
    let currentRightHeight = 0;
    let isFirstPage = true;

    for (const section of rightSectionsWithHeights) {
      const availableHeight = isFirstPage ? availableMainHeightFirstPage : availableMainHeightOtherPages;
      
      if (currentRightHeight + section.height > availableHeight && currentRightPage.length > 0) {
        rightPages.push([...currentRightPage]);
        currentRightPage = [section.html];
        currentRightHeight = section.height;
        isFirstPage = false;
      } else {
        currentRightPage.push(section.html);
        currentRightHeight += section.height;
      }
    }
    if (currentRightPage.length > 0) {
      rightPages.push([...currentRightPage]);
    }

    // Ensure we have at least one page
    if (rightPages.length === 0) {
      rightPages.push([]);
    }

    // Paginate left sections to match number of right pages
    const leftPages = [];
    if (leftSectionsWithHeights.length > 0) {
      let currentLeftPage = [];
      let currentLeftHeight = 0;
      let leftFirstPage = true;

      for (const section of leftSectionsWithHeights) {
        const availableHeight = leftFirstPage ? availableMainHeightFirstPage : availableMainHeightOtherPages;
        
        if (currentLeftHeight + section.height > availableHeight && currentLeftPage.length > 0) {
          leftPages.push([...currentLeftPage]);
          currentLeftPage = [section.html];
          currentLeftHeight = section.height;
          leftFirstPage = false;
        } else {
          currentLeftPage.push(section.html);
          currentLeftHeight += section.height;
        }
      }
      if (currentLeftPage.length > 0) {
        leftPages.push([...currentLeftPage]);
      }
    }

    // Ensure left pages match right pages count
    while (leftPages.length < rightPages.length) {
      leftPages.push([]);
    }
    while (leftPages.length > rightPages.length) {
      leftPages.pop();
    }

    // Create final pages
    const newPages = rightPages.map((right, index) => ({
      right: right,
      left: leftPages[index] || []
    }));

    setPages(newPages);
    setCurrentPage(prev => Math.min(prev, Math.max(0, newPages.length - 1)));
  }, [pageWidthPx, pageHeightPx, topBottomMargin, sideMargins, rightSectionOrder, leftSectionOrder, educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections, font, fontSize, headingSize, lineSpacing, sectionSpacing, paragraphSpacing, primaryColor, lineWeight, photo]);

  // Trigger pagination when content changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateContent();
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [paginateContent]);

  // Render left sidebar sections for display
  const renderLeftSectionContent = (sectionId) => {
    const textColor = '#F8F8F8';
    const isSidebar = true;
    
    switch (sectionId) {
      case 'about':
      case 'aboutMe':
        return (
          <Box key="about" sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
            <Avatar
              alt={`${data.firstName} ${data.lastName}`}
              src={photo || ""}
              sx={{ 
                width: 120, 
                height: 120, 
                margin: '0 auto', 
                mb: sectionSpacing / 10,
                border: `${lineWeight * 2}px solid ${textColor}`,
              }}
            />
            <Typography 
              variant="h5" 
              component="h2" 
              sx={getTypographyStyles(true, isSidebar, textColor)}
            >
              About Me
            </Typography>
            <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
              {savedSummary || data.profile}
            </Typography>
          </Box>
        );

      case 'contact':
        return (
          <Box key="contact" sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
            <Typography 
              variant="h5" 
              sx={getTypographyStyles(true, isSidebar, textColor)}
            >
              Contact
            </Typography>
            {data.phone && (
              <Box display="flex" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <PhoneIcon sx={{ mr: 1, color: textColor, fontSize: fontSize }} />
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  {data.phone}
                </Typography>
              </Box>
            )}
            {data.email && (
              <Box display="flex" alignItems="center" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <EmailIcon sx={{ mr: 1, color: textColor, fontSize: fontSize }} />
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  {data.email}
                </Typography>
              </Box>
            )}
            {data.city && (
              <Box display="flex" alignItems="center" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <LocationOnIcon sx={{ mr: 1, color: textColor, fontSize: fontSize }} />
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  {data.city}
                </Typography>
              </Box>
            )}
            {data.website && (
              <Box display="flex" alignItems="center" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <LanguageIcon sx={{ mr: 1, color: textColor, fontSize: fontSize }} />
                <a 
                  href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: textColor, 
                    textDecoration: 'none',
                    fontFamily: font,
                    fontSize: fontSize
                  }}
                >
                  {data.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              </Box>
            )}
            {data.linkedIn && (
              <Box display="flex" alignItems="center" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <LinkedInIcon sx={{ mr: 1, color: '#0077b5', fontSize: fontSize }} />
                <a 
                  href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: textColor, 
                    textDecoration: 'none',
                    fontFamily: font,
                    fontSize: fontSize
                  }}
                >
                  {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                </a>
              </Box>
            )}
            {data.drivingLicence && (
              <Box display="flex" alignItems="center" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <DriveEtaIcon sx={{ mr: 1, color: '#4caf50', fontSize: fontSize }} />
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  DL: {data.drivingLicence}
                </Typography>
              </Box>
            )}
            {savedWebsites.map((website, index) => (
              <Box key={index} display="flex" alignItems="center" mb={paragraphSpacing} sx={getContainerStyles(isSidebar)}>
                <LanguageIcon sx={{ mr: 1, color: textColor, fontSize: fontSize }} />
                <a 
                  href={website.url.startsWith('http') ? website.url : `https://${website.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: textColor, 
                    textDecoration: 'none',
                    fontFamily: font,
                    fontSize: fontSize
                  }}
                >
                  {website.name || website.url}
                </a>
              </Box>
            ))}
          </Box>
        );

      case 'skills':
        return (
          <Box key="skills" sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
            <Typography 
              variant="h5" 
              sx={getTypographyStyles(true, isSidebar, textColor)}
            >
              Skills
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'none', p: 0, ...getContainerStyles(isSidebar) }}>
              {savedSkills.length > 0 ? (
                savedSkills.map((skill, i) => (
                  <Box key={i} component="li" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                      {skill.name}
                    </Typography>
                  </Box>
                ))
              ) : (
                <>
                  <Box component="li" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>Adobe Creative Suite</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>UI/UX Design</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>Branding & Identity</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>Print & Digital Media</Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        );

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box key="languages" sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
            <Typography 
              variant="h5" 
              sx={getTypographyStyles(true, isSidebar, textColor)}
            >
              Languages
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'none', p: 0, ...getContainerStyles(isSidebar) }}>
              {savedLanguages.map((lang, i) => (
                <Box key={i} component="li" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                  <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                    {lang.name} {lang.level ? `- ${lang.level}` : ""}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'education':
        const getProgramType = (type) => {
          const types = {
            "UG": "Undergraduate",
            "PG": "Postgraduate",
            "Diploma": "Diploma",
            "Certificate": "Certificate"
          };
          return types[type] || type;
        };
        return (
          <Box key="education" sx={{ ...getContainerStyles(isSidebar), mb: sectionSpacing / 10 }}>
            <Typography 
              variant="h5" 
              sx={getTypographyStyles(true, isSidebar, textColor)}
            >
              Education
            </Typography>
            {educationEntries.length > 0 ? (
              educationEntries.map((edu) => (
                <Box key={edu.id} sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
                  <Typography 
                    variant="caption" 
                    sx={{
                      backgroundColor: edu.educationLevel === "school" ? "#1976d2" : "#9c27b0",
                      color: "white",
                      px: 1,
                      py: 0.3,
                      borderRadius: 1,
                      display: "inline-block",
                      mb: 1
                    }}
                  >
                    {edu.educationLevel === "school" ? "School" : "College"}
                  </Typography>
                  
                  {edu.educationLevel === "school" && (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {edu.standard === "10th" && "10th Standard"}
                        {edu.standard === "11th" && "11th Standard"}
                        {edu.standard === "12th" && "12th Standard"}
                        {edu.standard === "Other" && "Other Standard"}
                        {edu.standard && " - "}
                        {edu.schoolName}
                      </Typography>
                      <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                        📍 {edu.schoolLocation}
                        {edu.district && `, ${edu.district}`}
                      </Typography>
                      <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {edu.stream && `Stream: ${edu.stream}`}
                        {edu.stream && edu.percentage && " • "}
                        {edu.percentage && `Percentage: ${edu.percentage}%`}
                      </Typography>
                    </>
                  )}
                  
                  {edu.educationLevel === "college" && (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {edu.degreeName}
                        {edu.specialization && ` - ${edu.specialization}`}
                      </Typography>
                      <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {edu.collegeName}, {edu.collegeLocation}
                      </Typography>
                      <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {edu.programType && `Program: ${getProgramType(edu.programType)}`}
                        {edu.programType && edu.degreeType && " • "}
                        {edu.degreeType && `Degree: ${edu.degreeType}`}
                      </Typography>
                      <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {edu.cgpa && `CGPA: ${edu.cgpa}`}
                        {edu.cgpa && edu.percentage && " • "}
                        {edu.percentage && `Percentage: ${edu.percentage}%`}
                        {edu.grade && (edu.cgpa || edu.percentage) && " • "}
                        {edu.grade && `Grade: ${edu.grade}`}
                      </Typography>
                      {edu.gradMonth && edu.gradYear && (
                        <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                          🗓️ Graduated: {edu.gradMonth} {edu.gradYear}
                        </Typography>
                      )}
                      {edu.additionalCoursework && (
                        <Typography variant="body2" sx={{ 
                          ...getTypographyStyles(false, isSidebar, textColor),
                          mt: 1,
                          fontStyle: "italic"
                        }}>
                          Additional: {edu.additionalCoursework}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              ))
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                  B.F.A. in Graphic Design
                </Typography>
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  New York School of Design
                </Typography>
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  Graduated: May 2018
                </Typography>
              </>
            )}
          </Box>
        );

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          
          if (items && items.length > 0) {
            return (
              <Box key={sectionId} sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
                <Typography variant="h5" sx={getTypographyStyles(true, isSidebar, textColor)}>
                  {customSectionName.toUpperCase()}
                </Typography>
                {items.map((item, i) => (
                  <Typography key={i} sx={getTypographyStyles(false, isSidebar, textColor)}>
                    • {item}
                  </Typography>
                ))}
              </Box>
            );
          }
          return null;
        }
        
        return null;
    }
  };

  // Render right content sections for display
  const renderRightSectionContent = (sectionId) => {
    const textColor = '#333333';
    const isSidebar = false;
    const dustyBlue = primaryColor;
    
    switch (sectionId) {
      case 'experience':
        return (
          <div key="experience" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
              Experience
            </Typography>

            {workExperiences && workExperiences.length > 0 ? (
              workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={getContainerStyles(isSidebar)}>
                    <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                      {work.jobTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ ...getTypographyStyles(false, isSidebar, "text.secondary"), fontStyle: 'italic' }}>
                      {formatDate(work.startMonth, work.startYear)} -{" "}
                      {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" sx={{ ...getTypographyStyles(false, isSidebar, "text.secondary"), mb: paragraphSpacing, fontStyle: 'italic' }}>
                    {work.employer} | {work.location || ''}
                  </Typography>
                  <Typography sx={{ ...getTypographyStyles(false, isSidebar, textColor), whiteSpace: "pre-line" }}>
                    {work.description || "No description provided."}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{ mb: sectionSpacing / 10, ...getContainerStyles(isSidebar) }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={getContainerStyles(isSidebar)}>
                  <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                    Lead Designer
                  </Typography>
                  <Typography variant="body2" sx={{ ...getTypographyStyles(false, isSidebar, "text.secondary"), fontStyle: 'italic' }}>
                    Jan 2022 - Present
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ ...getTypographyStyles(false, isSidebar, "text.secondary"), mb: paragraphSpacing, fontStyle: 'italic' }}>
                  Creative Solutions Inc. | New York, NY
                </Typography>
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  • Led a team of 5 designers on major branding projects, increasing client satisfaction by 25%.
                </Typography>
                <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                  • Developed and executed design strategies that resulted in a 15% growth in new business.
                </Typography>
              </Box>
            )}
          </div>
        );
        
      case 'projects':
        return (
          <div key="projects" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            {savedCertifications.length > 0 && (
              <>
                <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
                  Certifications
                </Typography>
                {savedCertifications.map((cert, index) => (
                  <Box key={index} sx={{ mb: (paragraphSpacing * 2), ...getContainerStyles(isSidebar) }}>
                    <Box display="flex" alignItems="center" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                      <WorkIcon sx={{ mr: 1, color: dustyBlue, fontSize: fontSize }} />
                      <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {cert.name} - {cert.provider}
                      </Typography>
                    </Box>
                    <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                      • Completed in {cert.year}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {savedAccomplishments.length > 0 && (
              <>
                <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
                  Accomplishments
                </Typography>
                {savedAccomplishments.map((acc, index) => (
                  <Box key={index} sx={{ mb: (paragraphSpacing * 2), ...getContainerStyles(isSidebar) }}>
                    <Box display="flex" alignItems="center" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                      <WorkIcon sx={{ mr: 1, color: dustyBlue, fontSize: fontSize }} />
                      <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                        Accomplishment {index + 1}
                      </Typography>
                    </Box>
                    <Typography sx={{ ...getTypographyStyles(false, isSidebar, textColor), whiteSpace: "pre-line" }}>
                      {acc}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {savedVolunteering.length > 0 && (
              <>
                <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
                  Volunteering
                </Typography>
                {savedVolunteering.map((vol, index) => (
                  <Box key={index} sx={{ mb: (paragraphSpacing * 2), ...getContainerStyles(isSidebar) }}>
                    <Box display="flex" alignItems="center" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                      <WorkIcon sx={{ mr: 1, color: dustyBlue, fontSize: fontSize }} />
                      <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                        {vol.subtopic}
                      </Typography>
                    </Box>
                    <Typography sx={{ ...getTypographyStyles(false, isSidebar, textColor), whiteSpace: "pre-line" }}>
                      {vol.content}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {savedAccomplishments.length === 0 && savedCertifications.length === 0 && savedVolunteering.length === 0 && (
              <>
                <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
                  Projects & Accomplishments
                </Typography>
                
                <Box sx={{ mb: (paragraphSpacing * 2), ...getContainerStyles(isSidebar) }}>
                  <Box display="flex" alignItems="center" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <WorkIcon sx={{ mr: 1, color: dustyBlue, fontSize: fontSize }} />
                    <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                      E-commerce Brand Redesign
                    </Typography>
                  </Box>
                  <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                    • Led the full visual redesign of an e-commerce platform, improving user experience and increasing conversion rates by 10%.
                  </Typography>
                  <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                    • Created a comprehensive brand style guide, including new logo, color palette, and typography.
                  </Typography>
                </Box>
                
                <Box sx={getContainerStyles(isSidebar)}>
                  <Box display="flex" alignItems="center" sx={{ mb: paragraphSpacing, ...getContainerStyles(isSidebar) }}>
                    <WorkIcon sx={{ mr: 1, color: dustyBlue, fontSize: fontSize }} />
                    <Typography variant="h6" fontWeight="bold" sx={getTypographyStyles(false, isSidebar, textColor)}>
                      Mobile App UI/UX
                    </Typography>
                  </Box>
                  <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                    • Designed the complete user interface and user experience for a new mobile application.
                  </Typography>
                  <Typography sx={getTypographyStyles(false, isSidebar, textColor)}>
                    • Conducted user research and usability testing to inform design decisions and improve flow.
                  </Typography>
                </Box>
              </>
            )}
          </div>
        );

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <div key="softwareSkills" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
              Software Skills
            </Typography>
            {softwareSkills.map((skill, index) => (
              <Typography key={index} sx={getTypographyStyles(false, isSidebar, textColor)}>
                {skill.name} — {skill.level}%
              </Typography>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
              Interests
            </Typography>
            {savedInterests.map((interest, index) => (
              <Typography key={index} sx={getTypographyStyles(false, isSidebar, textColor)}>
                • {interest}
              </Typography>
            ))}
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
              Additional Information
            </Typography>
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={getTypographyStyles(false, isSidebar, textColor)}>
                {info}
              </Typography>
            ))}
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
              References
            </Typography>
            {savedReferences.map((ref, index) => (
              <Typography key={index} sx={{ ...getTypographyStyles(false, isSidebar, textColor), whiteSpace: "pre-line" }}>
                {ref}
              </Typography>
            ))}
          </div>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <div key="websites" className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
            <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
              Websites / Profiles
            </Typography>
            {savedWebsites.map((site, index) => (
              <Typography key={index} sx={getTypographyStyles(false, isSidebar, textColor)}>
                <a 
                  href={site.url.startsWith('http') ? site.url : `https://${site.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: textColor, textDecoration: 'none', fontFamily: font, fontSize: fontSize }}
                >
                  {site.name || site.url}
                </a>
              </Typography>
            ))}
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          
          if (items && items.length > 0) {
            return (
              <div key={sectionId} className="resume-section" style={{ marginBottom: sectionSpacing / 10, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: textColor }}>
                <Typography variant="h4" sx={getTypographyStyles(true, isSidebar, textColor)}>
                  {customSectionName.toUpperCase()}
                </Typography>
                {items.map((item, i) => (
                  <Typography key={i} sx={getTypographyStyles(false, isSidebar, textColor)}>
                    • {item}
                  </Typography>
                ))}
              </div>
            );
          }
          return null;
        }
        
        return null;
    }
  };

  const renderDragPanel = (side, sections) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          {side === 'left' ? 'Left Sidebar Sections' : 'Right Content Sections'}
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({sections.length} sections)
          </Typography>
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sections.length > 0 ? (
            sections.map((sectionId, index) => (
              <MuiPaper
                key={sectionId}
                draggable={enableDragDrop}
                onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index, side)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
                  boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                  cursor: enableDragDrop ? 'grab' : 'default',
                  '&:active': {
                    cursor: enableDragDrop ? 'grabbing' : 'default',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1 }}>
                  {getSectionTitle(sectionId)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {index + 1}
                </Typography>
              </MuiPaper>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No sections. Drag sections from the other side.
            </Typography>
          )}
        </Box>
        <Button 
          onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} 
          variant="outlined" 
          size="small" 
          sx={{ mt: 1 }}
        >
          Reset {side === 'left' ? 'Left' : 'Right'} Order
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", position: "relative", ...getContainerStyles() }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' }, ...getContainerStyles() }}>
          <IconButton 
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} 
            disabled={currentPage === 0} 
            color="primary" 
            sx={getContainerStyles()}
          >
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, ...getContainerStyles() }}>
            Page {currentPage + 1} of {Math.max(1, pages.length)}
          </Typography>
          <IconButton 
            onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} 
            disabled={currentPage === Math.max(0, pages.length - 1)} 
            color="primary" 
            sx={getContainerStyles()}
          >
            <ChevronRight />
          </IconButton>
          
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2, ...getContainerStyles() }}>
              <Button 
                onClick={handleArrangeSectionsClick}
                variant="contained" 
                startIcon={<Reorder />}
                color="primary"
              >
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      <Box id="resume-container" sx={getContainerStyles()}>
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Paper
              key={index}
              elevation={3}
              className="resume-page"
              sx={{ 
                borderRadius: 2, 
                width: page.width,
                minHeight: page.height,
                margin: `${topBottomMargin}px auto`,
                ...getContainerStyles(),
                display: exporting ? 'block' : (index === currentPage ? 'block' : 'none'),
                boxShadow: index === currentPage ? 3 : 0,
                overflow: 'hidden',
                '@media print': { 
                  display: 'block',
                  boxShadow: 'none',
                  borderRadius: 0,
                  margin: '0 auto',
                  pageBreakAfter: index < pages.length - 1 ? 'always' : 'auto',
                  ...getContainerStyles(),
                }
              }}
            >
              <Grid container sx={{ display: "flex", ...getContainerStyles() }}>
                {/* RIGHT CONTENT SECTION */}
                <Grid item xs={12} md={8} sx={{ 
                  p: `${topBottomMargin}px ${sideMargins}px`, 
                  width: "70%",
                  ...getContainerStyles(),
                }}>
                  {index === 0 && (
                    <Box sx={{ mb: sectionSpacing / 10, ...getContainerStyles() }}>
                      <Typography variant="h2" component="h1" fontWeight="bold" sx={{ 
                        color: primaryColor,
                        fontSize: `calc(${parseFloat(headingSize)} * 1.8)`,
                        fontFamily: font,
                        fontStyle: fontStyle,
                        lineHeight: `${lineSpacing * 1.2}px`,
                      }}>
                        {data.firstName ? data.firstName.toUpperCase() : ''} {data.lastName ? data.lastName.toUpperCase() : ''}
                      </Typography>
                      <Typography variant="h5" color={'#333333'} sx={{ 
                        letterSpacing: 2,
                        fontSize: `calc(${parseFloat(fontSize)} * 1.3)`,
                        fontFamily: font,
                        fontStyle: fontStyle,
                        lineHeight: `${lineSpacing}px`,
                        mt: 1,
                        mb: 1,
                      }}>
                        {data.currentPosition}
                      </Typography>
                    </Box>
                  )}
                  
                  {index === 0 && <Divider sx={{ 
                    my: sectionSpacing / 10, 
                    borderWidth: `${lineWeight}px`,
                    ...getContainerStyles(),
                  }} />}

                  <Box sx={getContainerStyles()}>
                    {pageContent.right.map((html, idx) => (
                      <div key={idx} dangerouslySetInnerHTML={{ __html: html }} />
                    ))}
                  </Box>
                </Grid>

                {/* LEFT SIDEBAR SECTION */}
                <Grid item xs={12} md={4} sx={{ 
                  ...getContainerStyles(true),
                  p: `${topBottomMargin}px ${sideMargins}px`, 
                  width: "30%",
                  height: "100%",   
                  minHeight: page.height, 
                }}>
                  {index === 0 && pageContent.left.length > 0 ? (
                    pageContent.left.map((html, idx) => (
                      <div key={idx} dangerouslySetInnerHTML={{ __html: html }} />
                    ))
                  ) : index === 0 && leftSectionOrder.map(sectionId => renderLeftSectionContent(sectionId))}
                  
                  {index > 0 && pageContent.left.length > 0 && (
                    pageContent.left.map((html, idx) => (
                      <div key={idx} dangerouslySetInnerHTML={{ __html: html }} />
                    ))
                  )}
                </Grid>
              </Grid>
            </Paper>
          ))
        ) : (
          <Paper elevation={3} sx={{ 
            borderRadius: 2, 
            width: page.width,
            minHeight: page.height,
            margin: `${topBottomMargin}px auto`,
            ...getContainerStyles(),
          }}>
            <Typography sx={{ p: 4, ...getContainerStyles() }}>Loading resume...</Typography>
          </Paper>
        )}
      </Box>

      <Dialog
        open={arrangeDialogOpen}
        onClose={handleArrangeDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder them within each side or move sections between left sidebar and right content.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, width: "50%" }}>
              {renderDragPanel("left", leftSectionOrder)}
            </Box>

            <Box sx={{ flex: 1, width: "50%" }}>
              {renderDragPanel("right", rightSectionOrder)}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">
            Reset All
          </Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Meta information for Resume11
export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

export default Resume11;